// Watchlist Page - User's bookmarked anime + continue watching
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Tabs,
  Tab,
  Button,
  Avatar,
  Skeleton,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Bookmark, PlayArrow, History, CheckCircle } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { removeFromContinueWatching, getContinueWatching, ContinueWatchItem } from '../../hooks/recent';
import { AnimeCard } from '../../components/ui/Card';
import { fetchAnimeDetail } from '../../hooks/useApi';

type TabValue = 0 | 1 | 2;

export default function Watchlist() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [tabValue, setTabValue] = React.useState<TabValue>(0);

  const bookmarks = profile?.bookmarks || [];
  const favorites = profile?.favorites || [];

  const continueWatching = React.useMemo(() => getContinueWatching(), [profile?.watchHistory?.length]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={900} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Bookmark /> My Watchlist
        </Typography>
      </Box>

      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v as TabValue)}
        sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <Bookmark fontSize="small" />
              <span>Saved</span>
              <Chip label={bookmarks.length} size="small" color="primary" />
            </Stack>
          }
        />
        <Tab
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <History fontSize="small" />
              <span>Favorites</span>
              <Chip label={favorites.length} size="small" color="primary" />
            </Stack>
          }
        />
        <Tab
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <PlayArrow fontSize="small" />
              <span>Continue Watching</span>
              <Chip label={continueWatching.length} size="small" color="primary" />
            </Stack>
          }
        />
      </Tabs>

      <AnimatePresence mode="wait">
        {/* Saved Tab */}
        {tabValue === 0 && (
          <motion.div
            key="saved"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {bookmarks.length > 0 ? (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)' },
                gap: 3
              }}>
                {bookmarks.map((id: number, index: number) => (
                  <SavedAnimeCard key={id} malId={id} index={index} />
                ))}
              </Box>
            ) : (
              <EmptyState
                icon={<Bookmark sx={{ fontSize: 64 }} />}
                title="No saved anime"
                description="Bookmark anime from the detail page to see them here"
              />
            )}
          </motion.div>
        )}

        {/* Favorites Tab */}
        {tabValue === 1 && (
          <motion.div
            key="favorites"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {favorites.length > 0 ? (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)' },
                gap: 3
              }}>
                {favorites.map((id: number, index: number) => (
                  <SavedAnimeCard key={id} malId={id} index={index} />
                ))}
              </Box>
            ) : (
              <EmptyState
                icon={<History sx={{ fontSize: 64 }} />}
                title="No favorites yet"
                description="Like anime from the detail page to add them here"
              />
            )}
          </motion.div>
        )}

        {/* Continue Watching Tab */}
        {tabValue === 2 && (
          <motion.div
            key="continue"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {continueWatching.length > 0 ? (
              <Stack spacing={2}>
                {continueWatching.map((item: ContinueWatchItem, index: number) => (
                  <motion.div
                    key={`${item.id}-${item.episode}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.03 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: 'var(--anime-token-surface-default)',
                        border: '1px solid var(--anime-token-border-default)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'var(--anime-token-brand-primary-main)',
                          backgroundColor: 'var(--anime-token-surface-hover)',
                        },
                      }}
                      onClick={() => navigate(`/watch/${item.id}?ep=${item.episode}&title=${encodeURIComponent(item.title)}`)}
                    >
                      <Box
                        component="img"
                        src={item.poster}
                        alt={item.title}
                        sx={{
                          width: 64, height: 36, borderRadius: 1,
                          objectFit: 'cover',
                          bgcolor: 'var(--anime-token-surface-variant)',
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700} noWrap>{item.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Episode {item.episode} · {item.type}
                        </Typography>
                      </Box>
                      <Button
                        variant="soft"
                        size="small"
                        startIcon={<PlayArrow fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/watch/${item.id}?ep=${item.episode}`);
                        }}
                      >
                        Resume
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFromContinueWatching(item.id, item.episode);
                        }}
                      >
                        ✕
                      </Button>
                    </Box>
                  </motion.div>
                ))}
              </Stack>
            ) : (
              <EmptyState
                icon={<PlayArrow sx={{ fontSize: 64 }} />}
                title="Nothing in progress"
                description="Start watching an anime to see your progress here"
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}

function EmptyState({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <Box sx={{ textAlign: 'center', py: 10 }}>
      <Box sx={{ color: 'var(--anime-token-text-tertiary)', opacity: 0.5, mb: 2 }}>{icon}</Box>
      <Typography variant="h6" color="text.tertiary">{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{description}</Typography>
    </Box>
  );
}

function SavedAnimeCard({ malId, index }: { malId: number; index: number }) {
  const { data } = useQuery({
    queryKey: ['anime-detail', malId],
    queryFn: async () => {
      const d = await fetchAnimeDetail(malId);
      return d.anime;
    },
    enabled: !!malId,
  });

  if (!data) {
    return (
      <Box sx={{ height: 300 }}>
        <Skeleton variant="rounded" width="100%" height={240} sx={{ borderRadius: 3, mb: 1 }} />
        <Skeleton width="80%" height={20} />
        <Skeleton width="50%" height={16} />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.5) }}
    >
      <AnimeCard
        id={data.id}
        title={data.title}
        poster={data.poster}
        score={data.score}
        type={data.type}
        episodes={data.episodes}
        isAdult={data.isAdult}
        onClick={() => window.location.href = `/anime/${data.id}`}
      />
    </motion.div>
  );
}